<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, Check, CheckCircle2, Headphones, HelpCircle, Lightbulb, RotateCcw, Volume2, X } from '@lucide/vue'
import { api, post, studyDone } from '../api'
import { isEnglish, t } from '../i18n'
import { effectsEnabled, playFeedback } from '../feedback'
import { speakGerman, stopGermanSpeech } from '../speech'

const router = useRouter()
const data = ref(null)
const index = ref(0)
const answer = ref('')
const result = ref(null)
const busy = ref(false)
const input = ref(null)
const extra = ref(false)
const error = ref('')
const replays = ref(0)
const combo = ref(0)
const masteryNotice = ref('')
const hintVisible = ref(false)
const speechToken = ref(0)
const phase = ref('quiz')
const studyWords = ref([])
const studyRevealed = ref(new Set())
const studyBusy = ref(false)
const selectedChoice = ref(null)
const items = computed(() => data.value?.items || [])
const current = computed(() => items.value[index.value])
const progress = computed(() => items.value.length ? ((index.value + (result.value ? 1 : 0)) / items.value.length) * 100 : 0)
const isArticleQuestion = computed(() => current.value?.dimension === 'article')
const isPosQuestion = computed(() => current.value?.dimension === 'part_of_speech')
const isPluralQuestion = computed(() => current.value?.dimension === 'plural')
const isPluralChoiceQuestion = computed(() => isPluralQuestion.value && current.value?.kind === 'recognition')
const correctChoice = computed(() => isArticleQuestion.value ? current.value?.word.article : isPosQuestion.value ? current.value?.word.part_of_speech : isPluralChoiceQuestion.value ? current.value?.word.plural_full : current.value?.word.meaning)
const genderNames = { der: '阳性', die: '阴性', das: '中性' }
function choiceLabel(choice) { return isArticleQuestion.value ? `${choice.text} · ${genderNames[choice.text] || ''}` : isPosQuestion.value ? (posNames[choice.text] || choice.text) : choice.text }
function isCorrectChoice(choice) {
  return (choice?.word_id != null && choice.word_id === current.value?.word?.id) || choice?.text === correctChoice.value
}
const posNames = { noun: '名词', verb: '动词', expression: '短语', adjective: '形容词', adverb: '副词' }
const l = computed(()=>isEnglish.value?{article:'Noun gender / article',pos:'Basic word class',plural:'Plural choice',oldPlural:'Legacy plural recall',meaning:'Meaning recognition',dictation:'Dictation',study:'Word preview',studyTitle:'Preview today’s new words',studyTip:'Tap “Show meaning” to check the translation and example, then start practice.',collapse:'Collapse all',expand:'Expand all',pluralLabel:'Plural',show:'Show meaning',hide:'Hide meaning',learned:'Done previewing — start practice',skip:'Skip — start practice',loading:'Preparing practice…',retry:'Retry today · ',listen:'Listen and write German',plays:'plays left',slow:'Play slowly',wait:'A German example appears after the first playback.',noExample:'No quality-checked example is available for this item. Answer from the audio first.',check:'Check answer',retryNotice:'This question will return later today.',combo:'correct in a row',comboDetail:'Your memory is growing new leaves',hintArticle:'Learn the article and noun together, for example: der Tisch.',hintPlural:'Learn die and the complete plural as one unit.',hint:'Recall first, then look at the answer — that builds stronger memory.'}:{article:'名词性别 / 冠词',pos:'基础词类判断',plural:'名词复数选择',oldPlural:'旧版名词复数回忆',meaning:'词义识别',dictation:'发音听写',study:'学前词表',studyTitle:'先过一遍今日新词',studyTip:'点击「显示释义」核对中文与例句，学完再进入考察。',collapse:'全部收起',expand:'全部展开',pluralLabel:'复数',show:'显示释义',hide:'收起释义',learned:'已学完，开始考察',skip:'跳过，直接考察',loading:'正在准备练习…',retry:'错题回访 · ',listen:'听一听，写出德语',plays:'次',slow:'慢速播放',wait:'首次播放结束后显示德语例句',noExample:'本题暂无通过质检的例句，请先根据发音作答。',check:'检查答案',retryNotice:'这道题会在今天稍后再考一次',combo:'连续答对',comboDetail:'记忆正在长出新叶',hintArticle:'把冠词和名词作为一个整体记忆，例如：der Tisch。',hintPlural:'把 die 和完整复数作为一个整体记忆。',hint:'先主动回忆，再看答案，记忆会更牢。'})
function posLabel(word) {
  if (word?.pos) return posNames[word.pos] || word.pos
  return word?.article ? '名词' : ''
}
const showDistractorNote = computed(() => {
  if (!result.value || result.value.correct) return false
  if (isArticleQuestion.value || isPosQuestion.value || current.value?.kind !== 'recognition') return false
  const choice = selectedChoice.value
  return !!(choice && choice.german && answer.value === choice.text)
})
const allRevealed = computed(() => studyWords.value.length > 0 && studyRevealed.value.size >= studyWords.value.length)
function taskName(item) {
  if (item?.dimension === 'article') return l.value.article
  if (item?.dimension === 'part_of_speech') return l.value.pos
  if (item?.dimension === 'plural') return item?.kind === 'recognition' ? l.value.plural : l.value.oldPlural
  if (item?.kind === 'recognition') return l.value.meaning
  if (item?.kind === 'dictation') return l.value.dictation
  return t('spelling')
}
const dictationHint = computed(() => {
  return current.value?.word.dictation_example?.cloze_de || ''
})
const dictationFullExample = computed(() => current.value?.word.dictation_example?.full_de || '')
const dictationSource = computed(() => current.value?.word.dictation_example || null)
const verbForms = computed(() => {
  const word = current.value?.word
  if (!word) return []
  return [word.third_person && `er/sie/es: ${word.third_person}`, word.preterite && `Präteritum: ${word.preterite}`, word.participle_ii && `${word.auxiliary || 'Partizip II'}: ${word.participle_ii}`].filter(Boolean)
})
function sourceLabel(source) {
  if (!source) return ''
  if (source.is_authoritative) return `教材原句 · ${source.source_ref}`
  if (source.source_type === 'tatoeba_cc_by') return `公开授权例句 · ${source.source_attribution} · ${source.source_license}`
  if (source.source_type === 'tatoeba_cc0') return `公开授权例句 · ${source.source_license}`
  return '整理例句 · 已通过语法与难度质检'
}

function speakText(text, slow = false) {
  if (!text) return
  const token = ++speechToken.value
  speakGerman(text, {
    rate: slow ? .68 : .9,
    onend: () => { if (token === speechToken.value && phase.value === 'quiz' && current.value?.kind === 'dictation') hintVisible.value = true },
    onerror: () => { if (token === speechToken.value && phase.value === 'quiz' && current.value?.kind === 'dictation') hintVisible.value = true },
  })
}
function speak(slow = false) {
  if (!current.value || replays.value >= 3) return
  speakText(current.value.word.german, slow)
  replays.value += 1
}
function speakStudy(word, slow = false) { speakText([word?.article, word?.german || word?.display].filter(Boolean).join(' '), slow) }
async function load() {
  try {
    const today = await api('/api/today')
    if (!today.locked) return router.replace('/')
    studyWords.value = Array.isArray(today.study_words) ? today.study_words : []
    phase.value = studyWords.value.length > 0 && !today.study_completed ? 'study' : 'quiz'
    const pending = today.items.filter((item) => !item.completed)
    if (!pending.length) {
      const more = await post('/api/today/extra-practice', { count: 10 })
      data.value = more; extra.value = true
    } else data.value = { items: pending }
    if (phase.value === 'quiz') {
      await nextTick(); input.value?.focus()
      if (current.value?.kind === 'dictation') speak()
    }
  } catch (e) { error.value = e.message }
}
async function submit(value = answer.value, dontKnow = false) {
  if ((!value && !dontKnow) || busy.value || result.value) return
  busy.value = true
  try {
    result.value = await post('/api/reviews', { item_id: current.value.id, answer: dontKnow ? '' : value, dont_know: dontKnow, idempotency_key: crypto.randomUUID() })
    answer.value = dontKnow ? '' : value
    combo.value = result.value.correct ? combo.value + 1 : 0
    playFeedback(result.value.plan_completed ? 'complete' : result.value.correct ? 'correct' : 'wrong')
  } catch (e) { error.value = e.message } finally { busy.value = false }
}
function selectChoice(choice) {
  if (result.value || busy.value) return
  answer.value = choice.text
  selectedChoice.value = choice
  submit(choice.text)
}
function toggleReveal(i) {
  const next = new Set(studyRevealed.value)
  if (next.has(i)) next.delete(i)
  else next.add(i)
  studyRevealed.value = next
}
function toggleRevealAll() {
  studyRevealed.value = allRevealed.value ? new Set() : new Set(studyWords.value.map((_, i) => i))
}
async function enterQuiz() {
  phase.value = 'quiz'
  await nextTick(); input.value?.focus()
  if (current.value?.kind === 'dictation') speak()
}
async function finishStudy() {
  if (studyBusy.value) return
  studyBusy.value = true
  try { await studyDone(); await enterQuiz() } catch (e) { error.value = e.message } finally { studyBusy.value = false }
}
function skipStudy() { enterQuiz() }
async function toggleMastery() {
  const word = current.value.word
  if (!word.mastered) {
    if (!window.confirm(`确认将“${word.display}”标记为完全掌握？之后仍会进行低频维护复习。`)) return
    await post(`/api/words/${word.id}/master`, { source: 'review_feedback' }); word.mastered = true; masteryNotice.value = '已加入低频维护复习，可随时撤销。'
  } else {
    await post(`/api/words/${word.id}/restore`); word.mastered = false; masteryNotice.value = '已恢复普通复习。'
  }
}
async function next() {
  const completedToday = result.value?.plan_completed
  speechToken.value += 1; stopGermanSpeech()
  result.value = null; answer.value = ''; replays.value = 0; hintVisible.value = false; selectedChoice.value = null
  if (index.value >= items.value.length - 1) {
    if (!extra.value) {
      try {
        const today = await api('/api/today')
        const pending = today.items?.filter((item) => !item.completed) || []
        if (pending.length) {
          data.value = { items: pending }; index.value = 0; masteryNotice.value = ''
          await nextTick(); input.value?.focus()
          if (current.value?.kind === 'dictation') speak()
          return
        }
      } catch (e) { error.value = e.message; return }
    }
    return router.replace(completedToday ? '/garden?celebrate=1' : '/')
  }
  index.value += 1
  masteryNotice.value = ''
  await nextTick(); input.value?.focus()
  if (current.value.kind === 'dictation') speak()
}
function submitFromKeyboard(e) {
  if (e.isComposing || busy.value) return
  e.preventDefault()
  result.value ? next() : submit()
}
function keydown(e) {
  if (phase.value !== 'quiz' || e.key !== 'Enter' || e.isComposing || busy.value) return
  e.preventDefault()
  result.value ? next() : submit()
}
watch(current, (item) => {
  if (!item?.word) return
  window.dispatchEvent(new CustomEvent('wortgarten-assistant-context', { detail: {
    word_id: item.word.id, item_id: item.id, german: item.word.german, article: item.word.article,
    chapter_number: item.word.chapter_number, code: item.word.course_code, dimension: item.dimension,
    answer_revealed: !!result.value,
  }}))
}, { immediate: true })
watch(result, (value) => {
  if (!current.value?.word) return
  window.dispatchEvent(new CustomEvent('wortgarten-assistant-context', { detail: {
    word_id: current.value.word.id, item_id: current.value.id, german: current.value.word.german,
    article: current.value.word.article, chapter_number: current.value.word.chapter_number,
    code: current.value.word.course_code, dimension: current.value.dimension, answer_revealed: !!value,
  }}))
})
onMounted(load)
onBeforeUnmount(() => { speechToken.value += 1; stopGermanSpeech() })
</script>

<template>
  <div class="practice-page" @keydown="keydown">
    <header class="practice-header"><button class="icon-button" @click="router.push('/')"><ArrowLeft /></button><div class="practice-progress"><span :style="{ width: `${progress}%` }"></span></div><small>{{ index + 1 }}/{{ items.length || 0 }}</small></header>
    <section v-if="phase === 'study'" class="study-panel glass-panel" :aria-label="l.study">
      <div class="study-head">
        <div class="study-title">
          <p class="eyebrow">{{l.study}} · {{ studyWords.length }}</p>
          <h1>{{l.studyTitle}}</h1>
          <p class="study-tip">{{l.studyTip}}</p>
        </div>
        <button class="reveal-all-button" @click="toggleRevealAll">{{ allRevealed ? l.collapse : l.expand }}</button>
      </div>
      <div class="study-list">
        <article v-for="(word, i) in studyWords" :key="i" :class="['study-card', { open: studyRevealed.has(i) }]">
          <div class="study-card-main">
            <div class="study-word">
              <h2>{{ word.display }}</h2>
              <small v-if="word.article || word.plural">{{ word.article || '' }} · {{l.pluralLabel}} {{ word.plural_full || word.plural || '—' }}</small>
            </div>
            <span v-if="posLabel(word)" class="study-pos">{{ posLabel(word) }}</span>
            <button class="audio-dot" :aria-label="`播放 ${word.display} 发音`" @click="speakStudy(word)"><Volume2 :size="18" /></button>
            <button class="reveal-button" @click="toggleReveal(i)">{{ studyRevealed.has(i) ? l.hide : l.show }}</button>
          </div>
          <div v-if="studyRevealed.has(i)" class="study-detail">
            <p class="study-chinese">{{ word.meaning }}</p>
            <p v-if="word.example_de" class="study-example"><b>{{ word.example_de }}</b><small v-if="word.example_translation">{{ word.example_translation }}</small></p>
            <p v-if="word.note_localized" class="study-note">{{ word.note_localized }}</p>
            <img v-if="word.image_url" class="study-image" :src="word.image_url" :alt="word.display" loading="lazy" />
          </div>
        </article>
      </div>
      <div class="study-actions">
        <button class="primary-button" :disabled="studyBusy" @click="finishStudy">{{l.learned}}</button>
        <button class="study-skip" @click="skipStudy">{{l.skip}}</button>
      </div>
      <p v-if="error" class="form-error">{{ error }}</p>
    </section>
    <template v-else>
    <div v-if="!current" class="loading-card">{{l.loading}}</div>
    <section v-else :class="['question-card', 'glass-panel', { 'has-result': !!result, 'feedback-correct': result?.correct && effectsEnabled(), 'feedback-wrong': result && !result.correct && effectsEnabled() }]">
      <div class="question-meta"><span class="glass-chip">{{ current.is_retry ? `错题回访 · ${taskName(current)}` : taskName(current) }}</span><span class="glass-chip">Kapitel {{ current.word.chapter_number || '·' }}</span></div>
      <div v-if="current.kind === 'dictation'" class="dictation-prompt">
        <button class="listen-button" :disabled="replays >= 3" @click="speak(false)"><Volume2 :size="32" /></button>
        <h1>{{l.listen}}</h1><p>{{ Math.max(0, 3-replays) }} {{l.plays}}</p>
        <button class="slow-button" :disabled="replays >= 3" @click="speak(true)"><Headphones :size="17" />{{l.slow}}</button>
        <p v-if="!hintVisible" class="hint-waiting">{{l.wait}}</p>
        <div v-else class="dictation-example"><Lightbulb :size="17" /><span v-if="dictationHint"><b>{{ dictationHint }}</b></span><span v-else>{{l.noExample}}</span></div>
      </div>
      <div v-else class="word-prompt">
        <p class="eyebrow">{{ isArticleQuestion ? (isEnglish?'Choose the noun gender / article':'请选择名词性别 / 冠词') : isPosQuestion ? '请选择这个词的基础词类' : isPluralChoiceQuestion ? (isEnglish?'Choose the full plural form':'看单数，选择正确的完整复数') : isPluralQuestion ? '旧版复数回忆' : current.kind === 'recognition' ? t('recognition') : t('spelling') }}</p>
        <h1>{{ isArticleQuestion || isPosQuestion ? current.word.german : isPluralQuestion ? `${current.word.article} ${current.word.german}` : current.kind === 'recognition' ? current.word.display : current.word.meaning }}</h1>
        <p v-if="isPluralChoiceQuestion" class="gender-guide">请选择完整复数形式；{{ current.word.plural_change_hint ? `变化提示：${current.word.plural_change_hint}` : '把 die 和复数形式一起记住。' }}</p>
        <p v-else-if="isPluralQuestion" class="gender-guide">这是已锁定任务中的旧版复数题；从下一份任务起将改为四选一完整复数。</p>
        <p v-if="isArticleQuestion" class="gender-guide"><span>der 阳性</span><span>die 阴性</span><span>das 中性</span></p>
        <button v-if="current.kind === 'recognition'" class="pronounce-inline" @click="speak()"><Volume2 :size="18" />听发音</button>
      </div>
      <div v-if="current.kind === 'recognition'" :class="['choices', { 'gender-choices': isArticleQuestion }]">
        <button v-for="choice in current.choices" :key="choice.text" :disabled="!!result" :data-article="isArticleQuestion ? choice.text : null" :class="{ selected: answer === choice.text, correct: result && isCorrectChoice(choice), wrong: result && answer === choice.text && !result.correct }" @click="selectChoice(choice)">{{ choiceLabel(choice) }}<Check v-if="result && isCorrectChoice(choice)" :size="18" /><X v-else-if="result && answer === choice.text && !result.correct" :size="18" /></button>
      </div>
      <div v-else class="spell-box">
        <input ref="input" v-model="answer" :disabled="!!result" autocomplete="off" autocapitalize="off" enterkeyhint="done" spellcheck="false" :placeholder="isEnglish?'Type German…':'输入德语…'" @keydown.enter.stop="submitFromKeyboard" />
        <div class="umlaut-row"><button v-for="letter in ['ä','ö','ü','ß']" :key="letter" :disabled="!!result" @click="answer += letter; input?.focus()">{{ letter }}</button></div>
        <button v-if="!result" class="primary-button" :disabled="!answer || busy" @click="submit()">{{l.check}}</button>
      </div>
      <button v-if="!result" class="dont-know-button" :disabled="busy" @click="submit('', true)"><HelpCircle :size="17" />{{t('dontKnow')}}</button>
      <div v-if="result" :class="['answer-feedback', result.correct ? 'is-correct' : 'is-wrong']">
        <span class="feedback-icon"><Check v-if="result.correct" /><RotateCcw v-else /></span>
        <div><b>{{ result.correct ? t('correct') : t('wrong') }}</b><p v-if="!result.correct">{{ result.expected }}</p><p v-if="showDistractorNote" class="distractor-note">{{isEnglish?'Your choice means':'你选的意思是'}} 「{{ selectedChoice.text }}」: <b>{{ selectedChoice.german }}</b></p><p v-if="current.kind === 'dictation' && dictationFullExample" class="revealed-example">{{ dictationFullExample }}</p><p v-else-if="result.correct && !isArticleQuestion && !isPosQuestion">{{ current.word.example_de || current.word.note_localized }}</p><small v-if="current.word.article || current.word.plural">{{ current.word.article }} {{ current.word.german }} · {{isEnglish?'Plural':'复数'}} {{ current.word.plural_full || current.word.plural || '—' }}</small><small v-if="verbForms.length" class="verb-forms">{{isEnglish?'Forms':'词形补充'}}: {{ verbForms.join(' · ') }}</small></div>
        <div class="feedback-actions"><button class="mastery-button" @click="toggleMastery"><RotateCcw v-if="current.word.mastered" :size="16"/><CheckCircle2 v-else :size="16"/>{{ current.word.mastered ? (isEnglish?'Restore':'撤销掌握') : (isEnglish?'Mastered':'完全掌握') }}</button><button class="primary-button" @click="next">{{ result.plan_completed ? (isEnglish?'Finish':'完成') : t('next') }}</button></div>
      </div>
      <p v-if="result?.retry_scheduled" class="retry-notice"><RotateCcw :size="15" />{{l.retryNotice}}</p>
      <div v-if="result?.correct && combo >= 2" class="combo-badge"><img src="/garden-stage-2.png" :alt="l.combo" class="art-matte"/><span><b>{{ combo }} {{l.combo}}</b><small>{{l.comboDetail}}</small></span></div>
      <p v-if="masteryNotice" class="success-note mastery-notice">{{ masteryNotice }}</p>
      <p v-if="error" class="form-error">{{ error }}</p>
    </section>
    </template>
  <aside v-if="phase === 'quiz' && current && !result" class="learning-hint"><Lightbulb :size="18" /><span>{{ isArticleQuestion ? '把冠词和名词作为一个整体记忆，例如：der Tisch。' : isPosQuestion ? '这是旧任务中的词类题；从下一份任务起不会再出现。' : isPluralChoiceQuestion ? '把 die 和完整复数作为一个整体记忆。' : isPluralQuestion ? '这是旧版复数题；从下一份任务起会改为四选一完整复数。' : current.word.note || '先主动回忆，再看答案，记忆会更牢。' }}</span></aside>
  </div>
</template>

<style scoped>
/* ============================================================
   Stage 3 · 练习核心页奶玻璃化（页面级覆盖，全局 styles.css 不动）
   性能纪律：全页 blur ≤ var(--glass-blur)(14px)，禁用重 blur；
   选项/输入等高频重绘元素一律走免 blur 的 --glass-bg-inset。
   ============================================================ */

/* —— 进度头：中玻璃胶囊条（练习路由下 App Bar 已隐藏，本条即页内顶栏） —— */
.practice-header{gap:12px;margin-bottom:22px;padding:6px 14px 6px 6px;border:1px solid var(--glass-border);border-radius:var(--radius-full);background:var(--glass-bg);-webkit-backdrop-filter:blur(var(--glass-blur)) saturate(160%);backdrop-filter:blur(var(--glass-blur)) saturate(160%);box-shadow:var(--glass-highlight),var(--glass-shadow)}
.practice-header small{font-variant-numeric:tabular-nums;font-weight:600}
.practice-progress{background:var(--glass-bg-inset)}

/* —— 练习卡片：玻璃面板承载，圆角/内边距按 App 密度收紧 —— */
.question-card{border-radius:var(--radius-xl);padding:clamp(20px,5vw,40px)}
.question-meta{margin-bottom:24px}

/* —— 选项按钮：免 blur 玻璃行，触控目标 ≥44px，状态色全部令牌化 —— */
.choices button{min-height:54px;border:1px solid var(--glass-border);border-radius:var(--radius-md);background:var(--glass-bg-inset);box-shadow:var(--glass-highlight);transition:border-color .2s ease,background .2s ease}
.choices button:hover:not(:disabled){border-color:var(--sage);background:var(--sage-soft)}
.choices button.selected{border-color:var(--sage);background:var(--sage-soft);color:var(--sage-dark);font-weight:700}
.choices button.correct{border-color:var(--sage);background:var(--sage-soft);color:var(--sage-dark);font-weight:700;box-shadow:0 6px 18px var(--sage-glow)}
.choices button.wrong{border-color:var(--red);background:color-mix(in srgb,var(--red) 13%,transparent);color:var(--red)}
.choices button:disabled:not(.correct):not(.wrong):not(.selected){opacity:.6}

/* —— 拼写输入：玻璃 inset 输入场；focus 环令牌化，暗色自动翻转可用 —— */
.spell-box>input{border:1px solid var(--glass-border);border-radius:var(--radius-md);background:var(--glass-bg-inset);padding:16px 18px;caret-color:var(--sage-dark)}
.spell-box>input:focus{border-color:var(--sage);box-shadow:0 0 0 3px var(--sage-glow)}
.spell-box>input::placeholder{color:var(--muted-2)}
.umlaut-row button{width:44px;height:44px;border:1px solid var(--glass-border);border-radius:12px;background:var(--glass-bg-inset);color:var(--ink)}
.umlaut-row button:hover:not(:disabled){border-color:var(--sage);background:var(--sage-soft)}

/* —— 听写区：发音主按钮换 on-accent 令牌，例句提示玻璃 inset —— */
.listen-button{color:var(--on-accent);box-shadow:0 14px 30px var(--sage-glow)}
.listen-button:disabled,.slow-button:disabled{opacity:.5}
.pronounce-inline,.slow-button{min-height:44px}
.dictation-example{background:var(--glass-bg-inset);border:1px solid var(--glass-border)}
.example-source a{color:var(--sage-dark);text-decoration:underline}

/* —— 反馈条：错误底色去硬编码（令牌 color-mix），状态图标实心令牌化 —— */
.answer-feedback.is-wrong{background:color-mix(in srgb,var(--red) 12%,transparent)}
.feedback-icon{box-shadow:var(--glass-highlight)}
.answer-feedback.is-correct .feedback-icon{background:var(--sage);color:var(--on-accent)}
.answer-feedback.is-wrong .feedback-icon{background:var(--red);color:var(--on-accent)}
.mastery-button{min-height:44px;border:1px solid var(--glass-border);background:var(--glass-bg-inset)}
.dont-know-button{min-height:44px}
.dont-know-button:hover:not(:disabled){background:var(--glass-bg-inset)}

/* —— 连对徽章：玻璃 inset；cream 底插画借 .art-matte 画板罩（暗色裱画） —— */
.combo-badge{background:var(--glass-bg-inset);border:1px solid var(--glass-border);box-shadow:var(--glass-shadow)}
.combo-badge img{border-width:3px;border-radius:12px}
.learning-hint>svg{color:var(--sage);flex:none}

/* —— 学前词表：玻璃面板承载；词条卡走免 blur 的 --glass-bg-inset（列表行纪律） —— */
.study-panel{border-radius:var(--radius-xl);padding:clamp(20px,5vw,40px);display:grid;gap:18px;align-content:start}
.study-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px}
.study-title h1{font:700 clamp(24px,4vw,34px)/1.2 var(--font-display);margin:2px 0 8px}
.study-tip{color:var(--muted);font-size:13px}
.reveal-all-button{flex:none;min-height:44px;padding:0 16px;border:1px solid var(--glass-border);border-radius:var(--radius-full);background:var(--glass-bg-inset);color:var(--sage-dark);font-weight:700;white-space:nowrap}
.reveal-all-button:hover{border-color:var(--sage);background:var(--sage-soft)}
.study-list{display:grid;gap:10px}
.study-card{border:1px solid var(--glass-border);border-radius:var(--radius-md);background:var(--glass-bg-inset);padding:14px 16px;display:grid;gap:12px;transition:border-color .2s ease}
.study-card.open{border-color:var(--sage)}
.study-card-main{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.study-word{flex:1;min-width:140px;display:grid;gap:2px}
.study-word h2{font:700 20px var(--font-display)}
.study-word small{color:var(--muted)}
.study-pos{padding:5px 9px;border-radius:9px;background:var(--sage-soft);color:var(--sage-dark);font-size:11px;font-weight:700}
.study-card .audio-dot{width:44px;height:44px;flex:none}
.reveal-button{margin-left:auto;min-height:44px;padding:0 14px;border:0;border-radius:12px;background:transparent;color:var(--sage-dark);font-weight:700}
.reveal-button:hover{background:var(--sage-soft)}
.study-detail{display:grid;gap:8px;border-top:1px solid var(--glass-border);padding-top:12px;animation:hint-reveal .28s ease-out}
.study-chinese{font-weight:700}
.study-example{display:grid;gap:3px}
.study-example b{font-weight:650;line-height:1.55}
.study-example small{color:var(--muted);line-height:1.5}
.study-note{color:var(--muted);font-size:13px;line-height:1.55}
.study-image{width:100%;max-height:180px;object-fit:cover;border-radius:12px}
.study-actions{position:sticky;bottom:max(12px,env(safe-area-inset-bottom));display:grid;gap:4px;padding:12px;border:1px solid var(--glass-border);border-radius:var(--radius-lg);background:var(--glass-bg);-webkit-backdrop-filter:blur(var(--glass-blur)) saturate(160%);backdrop-filter:blur(var(--glass-blur)) saturate(160%);box-shadow:var(--glass-highlight),var(--glass-shadow)}
.study-actions .primary-button{min-height:48px}
.study-skip{min-height:44px;border:0;background:transparent;color:var(--muted);font-weight:650;text-decoration:underline;text-underline-offset:3px}
.study-skip:hover{color:var(--ink)}

/* —— 答后辨析：所选干扰项归属提示 + 易混标签（令牌化，暗色自动翻转） —— */
.distractor-note{display:flex;align-items:center;flex-wrap:wrap;gap:6px;font-size:13px;margin-top:6px}
.distractor-note b{color:var(--ink)}
.confusion-tag{padding:3px 9px;border-radius:99px;background:var(--sage-soft);color:var(--sage-dark);font-size:11px;font-weight:700}

/* —— 键盘可达性：全页按钮统一 focus-visible 焦点环（令牌，暗色可用） —— */
.practice-page button:focus-visible{outline:2px solid var(--sage);outline-offset:2px}
/* On a phone, feedback can be taller than the viewport (especially after a
   dictation hint). Keep the only forward action in thumb reach instead of
   making the learner scroll past the entire explanation. */
@media(max-width:600px){
  .question-card.has-result{padding-bottom:148px}
  .question-card.has-result .feedback-actions{position:fixed;z-index:50;left:16px;right:16px;bottom:max(14px,env(safe-area-inset-bottom));display:grid;grid-template-columns:minmax(0,.9fr) minmax(0,1.1fr);gap:9px;padding:9px;border:1px solid var(--glass-border);border-radius:18px;background:var(--glass-bg-strong);-webkit-backdrop-filter:blur(var(--glass-blur-strong)) saturate(160%);backdrop-filter:blur(var(--glass-blur-strong)) saturate(160%);box-shadow:var(--glass-highlight),0 16px 38px rgba(50,60,44,.2)}
  .question-card.has-result .feedback-actions button{min-height:50px}
  .question-card.has-result .feedback-actions .primary-button{font-size:17px}
  .answer-feedback{align-items:start}
  .answer-feedback>div:nth-child(2){min-width:0}
}

/* —— 暗色补充：词性导引人造色无对应色板令牌，仅暗色提亮覆盖 —— */
[data-theme="dark"] .gender-guide span:nth-child(1){color:#9cc0d8}
[data-theme="dark"] .gender-guide span:nth-child(2){color:#dda7b0}
[data-theme="dark"] .gender-guide span:nth-child(3){color:#d8c988}
[data-theme="dark"] .gender-choices button[data-article=der]{border-bottom-color:#7fa8c4}
[data-theme="dark"] .gender-choices button[data-article=die]{border-bottom-color:#d499a4}
[data-theme="dark"] .gender-choices button[data-article=das]{border-bottom-color:#cdbf7f}

/* —— 降级：不支持 backdrop-filter 时进度头与词表操作区回退近实色 —— */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))){
  .practice-header,.study-actions{background:var(--glass-fallback);-webkit-backdrop-filter:none;backdrop-filter:none}
}
</style>
